import React, { useEffect, useRef, useState } from 'react';
import { AllocationMember, MonthlyAllocation } from '../types/allocation';
import { ProjectCost } from '../types/api';
import type { MemberCosts } from '../services/memberCostService';
import {
  DEFAULT_PROFIT_PERCENT,
  caretAfterFormat,
  formatAmount,
  formatAmountInput,
  parseAmount,
  profitAmount,
  profitPercentFromRevenue,
  revenueFromProfitPercent,
  round2,
  significantBeforeCaret,
} from '../utils/profitability';
import { getMemberRateForMonth, getMemberMonthlyRevenue } from '../utils/costUtils';

interface CostSummaryProps {
  allocationMembers: AllocationMember[];
  months: MonthlyAllocation[];
  projectCosts: ProjectCost[];
  memberCosts?: MemberCosts; // Month-specific costs
  endDate?: string;
  /** Quote margin on revenue. Defaults to 40% when the parent tracks no value. */
  profitPercent?: number;
  /**
   * What to quote, before tax. Pass null/undefined to have it derived from
   * profitPercent and seeded back to the parent via onProfitabilityChange.
   */
  revenue?: number | null;
  /** Omit to render the profitability figures read-only (e.g. a saved view). */
  onProfitabilityChange?: (next: { profitPercent: number; revenue: number }) => void;
}

const CostSummary: React.FC<CostSummaryProps> = ({
  allocationMembers,
  months,
  projectCosts,
  memberCosts,
  endDate,
  profitPercent,
  revenue,
  onProfitabilityChange,
}) => {
  // Rate a member's hours are costed at - the TMIS employee cost rate
  const getMemberCostForMonth = (memberId: number, monthStart: string): number => {
    const member = allocationMembers.find(m => m.id === memberId);
    return getMemberRateForMonth(member, memberCosts, monthStart);
  };

  // Calculate monthly costs for each member (using month-specific rates)
  const calculateMonthlyCosts = () => {
    const monthlyCosts: { [monthStart: string]: number } = {};
    
    months.forEach(month => {
      let monthTotal = 0;
      allocationMembers.forEach(member => {
        const hours = member.monthlyAllocations[month.monthStart] || 0;
        const costForMonth = getMemberCostForMonth(member.id, month.monthStart);
        const memberCost = hours * costForMonth;
        monthTotal += memberCost;
      });
      monthlyCosts[month.monthStart] = monthTotal;
    });
    
    return monthlyCosts;
  };

  // Calculate total employee costs (using month-specific rates)
  const calculateTotalEmployeeCosts = () => {
    let total = 0;
    allocationMembers.forEach(member => {
      months.forEach(month => {
        const hours = member.monthlyAllocations[month.monthStart] || 0;
        const costForMonth = getMemberCostForMonth(member.id, month.monthStart);
        total += hours * costForMonth;
      });
    });
    return total;
  };

  // Calculate total project costs (sum all monthly costs across all categories)
  const calculateTotalProjectCosts = () => {
    return projectCosts.reduce((total, cost) => {
      const costTotal = Object.values(cost.monthlyCosts || {}).reduce((sum, val) => sum + (val || 0), 0);
      return total + costTotal;
    }, 0);
  };

  // Calculate billable project costs
  const calculateBillableProjectCosts = () => {
    return projectCosts.filter(cost => cost.billable)
      .reduce((total, cost) => {
        const costTotal = Object.values(cost.monthlyCosts || {}).reduce((sum, val) => sum + (val || 0), 0);
        return total + costTotal;
      }, 0);
  };

  // Calculate non-billable project costs
  const calculateNonBillableProjectCosts = () => {
    return projectCosts.filter(cost => !cost.billable)
      .reduce((total, cost) => {
        const costTotal = Object.values(cost.monthlyCosts || {}).reduce((sum, val) => sum + (val || 0), 0);
        return total + costTotal;
      }, 0);
  };

  // Revenue the allocation grid implies: every member's hours at their client rate
  const calculateTotalRevenue = () => {
    let total = 0;
    allocationMembers.forEach(member => {
      months.forEach(month => {
        total += getMemberMonthlyRevenue(member, memberCosts, month.monthStart);
      });
    });
    return total;
  };

  // Calculate total project cost
  const calculateTotalProjectCost = () => {
    const employeeCosts = calculateTotalEmployeeCosts();
    const totalProjectCosts = calculateTotalProjectCosts();
    return employeeCosts + totalProjectCosts;
  };

  // Calculate total hours per month
  const calculateMonthlyHours = () => {
    const monthlyHours: { [monthStart: string]: number } = {};
    
    months.forEach(month => {
      let monthTotal = 0;
      allocationMembers.forEach(member => {
        monthTotal += member.monthlyAllocations[month.monthStart] || 0;
      });
      monthlyHours[month.monthStart] = monthTotal;
    });
    
    return monthlyHours;
  };

  const monthlyCosts = calculateMonthlyCosts();
  const monthlyHours = calculateMonthlyHours();
  const totalEmployeeCosts = calculateTotalEmployeeCosts();
  const totalProjectCosts = calculateTotalProjectCosts();
  const billableCosts = calculateBillableProjectCosts();
  const nonBillableCosts = calculateNonBillableProjectCosts();
  const totalProjectCost = calculateTotalProjectCost();
  const totalRevenueCost = calculateTotalRevenue();
  // Profit the grid's client rates earn over what the project costs
  const revenueProfitPercent = profitPercentFromRevenue(totalProjectCost, totalRevenueCost);

  // --- Profitability (what to quote) --------------------------------------
  // The parent owns the committed values so it can save them with the scenario;
  // the drafts below are just what is currently typed in the boxes.
  const editable = typeof onProfitabilityChange === 'function';
  const effectivePercent = profitPercent ?? DEFAULT_PROFIT_PERCENT;
  const effectiveRevenue =
    revenue === undefined || revenue === null
      ? revenueFromProfitPercent(totalProjectCost, effectivePercent)
      : revenue;

  const [percentDraft, setPercentDraft] = useState<string>(String(round2(effectivePercent)));
  // Always grouped - including mid-typing, via handleRevenueChange below.
  const [revenueDraft, setRevenueDraft] = useState<string>(formatAmount(round2(effectiveRevenue)));

  useEffect(() => {
    setPercentDraft(String(round2(effectivePercent)));
  }, [effectivePercent]);

  useEffect(() => {
    setRevenueDraft(formatAmount(round2(effectiveRevenue)));
  }, [effectiveRevenue]);

  // Seed the parent with the default quote when it holds no revenue yet, and
  // requote when the underlying cost moves (profit % is what stays fixed).
  // A saved scenario arrives with a revenue already set, so it is left alone.
  const lastCostRef = useRef<number | null>(null);
  useEffect(() => {
    const previousCost = lastCostRef.current;
    lastCostRef.current = totalProjectCost;
    if (!onProfitabilityChange) {
      return;
    }
    const costChanged = previousCost !== null && previousCost !== totalProjectCost;
    const needsSeed = revenue === undefined || revenue === null;
    if (!costChanged && !needsSeed) {
      return;
    }
    onProfitabilityChange({
      profitPercent: round2(effectivePercent),
      revenue: round2(revenueFromProfitPercent(totalProjectCost, effectivePercent)),
    });
  }, [totalProjectCost, effectivePercent, revenue, onProfitabilityChange]);

  const commitPercent = () => {
    const parsed = parseFloat(percentDraft);
    const nextPercent = Number.isFinite(parsed) ? round2(parsed) : 0;
    const nextRevenue = round2(revenueFromProfitPercent(totalProjectCost, nextPercent));
    setPercentDraft(String(nextPercent));
    setRevenueDraft(formatAmount(nextRevenue));
    onProfitabilityChange?.({ profitPercent: nextPercent, revenue: nextRevenue });
  };

  const commitRevenue = () => {
    const nextRevenue = round2(parseAmount(revenueDraft));
    const nextPercent = round2(profitPercentFromRevenue(totalProjectCost, nextRevenue));
    setRevenueDraft(formatAmount(nextRevenue));
    setPercentDraft(String(nextPercent));
    onProfitabilityChange?.({ profitPercent: nextPercent, revenue: nextRevenue });
  };

  // Regroup on every keystroke, then put the caret back where the user left it:
  // inserting a comma shifts every character after it, so the position is
  // tracked by how many digits precede the caret rather than by raw offset.
  const revenueInputRef = useRef<HTMLInputElement>(null);
  const revenueCaretRef = useRef<number | null>(null);

  const handleRevenueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const caret = event.target.selectionStart ?? raw.length;
    const formatted = formatAmountInput(raw);
    revenueCaretRef.current = caretAfterFormat(formatted, significantBeforeCaret(raw, caret));
    setRevenueDraft(formatted);
  };

  useEffect(() => {
    if (revenueCaretRef.current !== null && revenueInputRef.current) {
      const position = revenueCaretRef.current;
      revenueInputRef.current.setSelectionRange(position, position);
      revenueCaretRef.current = null;
    }
  });

  const quotedProfit = profitAmount(totalProjectCost, effectiveRevenue);

  // Create breakdown data for monthly costs
  const monthlyBreakdown = months.map(month => {
    const employeeCosts = monthlyCosts[month.monthStart] || 0;
    // Sum project costs for this specific month
    const projectCostsForMonth = projectCosts.reduce((total, cost) => {
      return total + (cost.monthlyCosts?.[month.monthStart] || 0);
    }, 0);
    
    return {
      month: month.label,
      monthStart: month.monthStart,
      employeeCosts,
      projectCosts: projectCostsForMonth,
      totalCost: employeeCosts + projectCostsForMonth,
      hours: monthlyHours[month.monthStart] || 0,
    };
  });

  if (allocationMembers.length === 0) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow p-6 border border-gray-200/50">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Cost Summary</h3>
        <div className="text-center py-8 text-gray-500">
          <p>No team members added yet. Add members to see cost calculations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow border border-gray-200/50">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Cost Summary</h3>
        <p className="mt-1 text-sm text-gray-600">
          Overview of project costs and monthly breakdown
        </p>
      </div>

      <div className="p-6">
        {/* Total Cost Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-blue-600">Total Employee Costs</div>
            <div className="text-2xl font-bold text-blue-900">
              ₹{totalEmployeeCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-green-600">Billable Costs</div>
            <div className="text-2xl font-bold text-green-900">
              ₹{billableCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </div>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-red-600">Non-billable Costs</div>
            <div className="text-2xl font-bold text-red-900">
              ₹{nonBillableCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-gray-600">Total Project Cost</div>
            <div className="text-2xl font-bold text-gray-900">
              ₹{totalProjectCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </div>
          </div>

          <div className="bg-indigo-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-indigo-600">Total Revenue Cost</div>
            <div className="text-2xl font-bold text-indigo-900">
              ₹{totalRevenueCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </div>
            <div className="mt-1 text-xs text-indigo-700">Hours × client rate</div>
          </div>

          <div className={`p-4 rounded-lg ${revenueProfitPercent < 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
            <div className={`text-sm font-medium ${revenueProfitPercent < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              Profitability %
            </div>
            <div className={`text-2xl font-bold ${revenueProfitPercent < 0 ? 'text-red-900' : 'text-emerald-900'}`}>
              {round2(revenueProfitPercent).toLocaleString('en-IN', { maximumFractionDigits: 2 })}%
            </div>
            <div className={`mt-1 text-xs ${revenueProfitPercent < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
              ₹{profitAmount(totalProjectCost, totalRevenueCost).toLocaleString('en-IN', { maximumFractionDigits: 0 })} over cost
            </div>
          </div>
        </div>

        {/* Cost Breakdown */}
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-900 mb-3">Cost Breakdown</h4>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Employee Costs:</span>
                <span className="ml-2 font-medium">₹{totalEmployeeCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
              </div>
              <div>
                <span className="text-gray-600">Project Costs (Total):</span>
                <span className="ml-2 font-medium">₹{totalProjectCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
              </div>
              <div>
                <span className="text-gray-600">Billable Costs:</span>
                <span className="ml-2 font-medium text-green-600">₹{billableCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
              </div>
              <div>
                <span className="text-gray-600">Non-billable Costs:</span>
                <span className="ml-2 font-medium text-red-600">₹{nonBillableCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
              </div>
              <div className="border-t pt-2 md:border-t-0 md:pt-0 md:col-span-2">
                <span className="text-gray-900 font-semibold">Total Project Cost:</span>
                <span className="ml-2 font-bold text-lg">₹{totalProjectCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Profitability */}
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-900 mb-3">Profitability</h4>
          <div className="bg-amber-50/60 border border-amber-200 p-4 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="profit-percent" className="block text-sm font-medium text-gray-700 mb-1">
                  Profit %
                </label>
                <input
                  id="profit-percent"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={percentDraft}
                  readOnly={!editable}
                  aria-describedby="profitability-note"
                  onChange={(e) => setPercentDraft(e.target.value)}
                  onBlur={commitPercent}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                  className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500 focus:outline-none transition-all ${
                    editable ? '' : 'bg-gray-100 cursor-not-allowed'
                  }`}
                />
              </div>
              <div>
                <label htmlFor="quote-revenue" className="block text-sm font-medium text-gray-700 mb-1">
                  Revenue in INR (before tax)
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500">
                    ₹
                  </span>
                  <input
                    id="quote-revenue"
                    ref={revenueInputRef}
                    type="text"
                    inputMode="decimal"
                    value={revenueDraft}
                    readOnly={!editable}
                    aria-describedby="profitability-note"
                    onChange={handleRevenueChange}
                    onBlur={commitRevenue}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                    }}
                    className={`w-full pl-7 pr-3 py-2 text-sm text-right tabular-nums border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500 focus:outline-none transition-all ${
                      editable ? '' : 'bg-gray-100 cursor-not-allowed'
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Resulting profit, so the two inputs can be sanity-checked at a glance */}
            <div className="mt-3 pt-3 border-t border-amber-200 text-sm">
              <span className="text-gray-600">Profit on a quote of </span>
              <span className="font-medium">
                ₹{effectiveRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </span>
              <span className="text-gray-600">: </span>
              <span className={`font-semibold ${quotedProfit < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {quotedProfit < 0 ? '-' : ''}₹
                {Math.abs(quotedProfit).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </span>
              {quotedProfit < 0 && (
                <span className="ml-2 text-red-600">(quoted below cost)</span>
              )}
            </div>

            <p id="profitability-note" className="mt-3 text-xs text-amber-900">
              <strong>Note:</strong> Profit % is a margin on Revenue, so{' '}
              <em>Profit % = (Revenue − Total Project Cost) ÷ Revenue × 100</em> and{' '}
              <em>Revenue = Total Project Cost ÷ (1 − Profit % ÷ 100)</em>. Edit either
              box and the other updates when you click away; the default is {DEFAULT_PROFIT_PERCENT}%.
              Quoting below cost gives a negative Profit %. Revenue is before tax and excludes GST.
            </p>
          </div>
        </div>

        {/* Monthly Breakdown */}
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-3">Monthly Breakdown</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Hours
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee Costs
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project Costs
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monthly Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {monthlyBreakdown.map((breakdown) => (
                  <tr key={breakdown.monthStart} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{breakdown.month}</div>
                        <div className="text-xs text-gray-500">{breakdown.monthStart}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                      {breakdown.hours.toFixed(1)} hrs
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                      ₹{breakdown.employeeCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600 text-right">
                      ₹{breakdown.projectCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      ₹{breakdown.totalCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                  </tr>
                ))}
                
                {/* Totals Row */}
                <tr className="bg-gray-100 font-semibold">
                  <td className="px-3 py-3 text-sm text-gray-900">
                    Total
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900 text-right">
                    {monthlyBreakdown.reduce((sum, b) => sum + b.hours, 0).toFixed(1)} hrs
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900 text-right">
                    ₹{totalEmployeeCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900 text-right">
                    ₹{totalProjectCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900 text-right">
                    ₹{totalProjectCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Cost Formula Explanation */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h5 className="text-sm font-medium text-blue-900 mb-2">Cost Calculation Formula</h5>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Employee Monthly Cost:</strong> allocated_hours × hourly rate for client</p>
            <p><strong>Total Employee Costs:</strong> sum of all employee monthly costs</p>
            <p><strong>Project Costs:</strong> sum of all monthly project costs per category</p>
            <p><strong>Total Project Cost:</strong> Employee Costs + Project Costs (Billable + Non-billable)</p>
            <p><strong>Monthly Total:</strong> Employee Costs + Project Costs for that month</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostSummary;
